import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import ReactModal from 'react-modal';

import { ComparisonRoles } from '../../../../adl-gen/totalinteraction/futurefit/frontend';
import { withI18n, WithI18nProps } from '../../../../internationalization/i18n-hoc';
import { i18next } from '../../../../internationalization/i18next';
import { CloseIcon, FilterSVGIcon } from '../../../common/icons';
import { Button } from '../../../widgets/button/button';
import { Item, SearchableRadioItemSet } from '../../../widgets/searchable-radio-item-set/searchable-radio-item-set';
import { InputState, newTextField } from '../../../widgets/text-input/text-field';
import { TextInput } from '../../../widgets/text-input/text-input';

ReactModal.setAppElement('#root');

const styles = require('./roles-filter.css');

interface RolesFilterProps extends WithI18nProps {
  initial?: string;
  defaultRoleId: string;
  otherRoleIds: string[];
  rolesMap: ComparisonRoles['roles'];

  onCancel(): void;
  onDone(selected?: string): void;
}

interface Toggle {
  label: string;
  detail: string;
}

@observer
class RolesFilter0 extends React.Component<RolesFilterProps> {
  @observable
  searchState: InputState = textFieldState();

  @observable
  roleSelected?: string = this.props.initial;

  render() {
    return (
      <ReactModal
        isOpen={true}
        shouldCloseOnEsc={true}
        shouldCloseOnOverlayClick={true}
        onRequestClose={this.props.onCancel}
        className={styles.modal}
      >
        {this.renderCloseButton()}
        {this.renderTitle()}
        {this.renderSearch()}
        {this.renderRadioItems()}
        <br />
        {this.renderButtons()}
      </ReactModal>
    );
  }

  renderCloseButton(): JSX.Element {
    return (
      <div className={styles.closeButton}>
        <CloseIcon onClick={this.props.onCancel} size={'xsmall'} />
      </div>
    );
  }

  renderTitle(): JSX.Element {
    return (
      <div className={styles.title}>
        <FilterSVGIcon className={styles.filterIcon} size={'small'} />
        <div className={styles.titleText}>{i18next.t('filters.roles.title', 'Roles Comparison')}</div>
      </div>
    );
  }

  renderSearch(): JSX.Element {
    return (
      <div className={styles.search}>
        <TextInput placeholder={i18next.t('filters.placeholder', 'Search')} state={this.searchState} type={'text'} />
      </div>
    );
  }

  renderRadioItems(): JSX.Element {
    /// We are going to put the default role first in the list, but we don't want it to be
    /// duplicated further down, so weed it out if it's there
    const defaultItemIdx = this.items.findIndex(item => item.id === this.props.defaultRoleId);
    if (defaultItemIdx >= 0) {
      this.items.splice(defaultItemIdx, 1);
    }
    /// Case-insensitive search - first clean up the expression so it's a valid regex
    const term = this.searchState.value().replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    const re = new RegExp(term, 'i');
    this.items.sort((a, b) => a.value.label.localeCompare(b.value.label));
    return (
      <div className={styles.radioItems}>
        <SearchableRadioItemSet
          items={[this.idToItem(this.props.defaultRoleId, this.roleSelected || this.props.defaultRoleId)]
            .concat(this.items)
            .filter(i => i.value.label.search(re) !== -1)}
          hintText={t => t.detail}
          label={t => t.label}
          onChange={this.onChange}
        />
      </div>
    );
  }

  renderButtons(): JSX.Element {
    return (
      <div className={styles.buttons}>
        <Button
          variant={'tertiary'}
          onClick={async () => this.onClear()}
        >
          {i18next.t('filters.clear', 'Clear Filter')}
        </Button>
        <Button
          onClick={async () => this.props.onDone(this.roleSelected)}
          disabled={false}
        >
          {this.roleSelected && this.roleSelected !== this.props.defaultRoleId
            ? i18next.t('filters.roles.compare', {
                value: this.roleToLabel(this.roleSelected),
                defaultValue: `Compare against ${this.roleToLabel(this.roleSelected)}`
              })
            : i18next.t('filters.roles.clear', 'Clear role comparison')}
        </Button>
      </div>
    );
  }

  @action
  onClear(): void {
    this.roleSelected = undefined;
  }

  @action
  onChange = (onChangeId: string): void => {
    this.roleSelected = (onChangeId === this.props.defaultRoleId) ? undefined : onChangeId;
  };

  @computed
  get allIds(): string[] {
    return this.props.otherRoleIds;
  }

  @computed
  get items(): Item<Toggle>[] {
    return this.allIds.map(roleIds => this.idToItem(roleIds, this.roleSelected));
  }

  idToItem(id: string, selected?: string): Item<Toggle> {
    return {
      id,
      selected: selected === id,
      value: this.roleToToggle(id),
    };
  }

  roleToToggle(id: string) {
    return {
      label: this.roleToLabel(id),
      detail: ''
    };
  }

  roleToLabel(id: string) {
    const { rolesMap, t } = this.props;
    return t(rolesMap[id].workRole.value.name);
  }
}

export const RolesFilter = withI18n(RolesFilter0);

function textFieldState(initialValue: string = '') {
  const state = newTextField();
  state.onChange(initialValue);
  return state;
}



// WEBPACK FOOTER //
// ./src/ui/components/filters/roles-filter/roles-filter.tsx